import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ExternalLink, ShieldAlert, Link2, RefreshCw } from "lucide-react";

/**
 * Patient Link (P-Link) — embeds https://kivihealth.com/doctor/login inside the
 * Vyomra HOS shell.  KiviHealth's CSP restricts framing to its own domains, so
 * we optimistically render the iframe and, if the frame's `load` event never
 * fires within a short window, we swap in a clean fallback with an "Open
 * KiviHealth securely" button.
 *
 * No credentials are stored, no requests are proxied, no CSP is bypassed —
 * strictly a client-side attempt-then-fallback shell.
 */
const KIVI_URL = "https://kivihealth.com/doctor/login";

export default function PatientLink() {
  // "loading" → iframe requested but not yet reported success
  // "embedded" → iframe fired onLoad successfully (frame is visible)
  // "blocked"  → timeout expired without a load event, or the frame reported an error
  const [state, setState] = useState("loading");
  const [key, setKey] = useState(0);       // bump to force iframe remount
  const iframeRef = useRef(null);

  useEffect(() => {
    setState("loading");
    // If the iframe's `load` event never fires within 4 s we assume the
    // browser refused to render it (CSP frame-ancestors or X-Frame-Options).
    const t = setTimeout(() => {
      setState((cur) => (cur === "loading" ? "blocked" : cur));
    }, 4000);
    return () => clearTimeout(t);
  }, [key]);

  const handleLoad = () => {
    // Successful load → cross-origin so we can't read anything but we know it
    // rendered.  Some browsers fire onLoad even on the CSP-block error page —
    // detect that by inspecting the frame's dimensions; a proper page has a
    // non-zero body height, whereas the block-error page has 0.
    setTimeout(() => {
      const f = iframeRef.current;
      if (!f) return;
      try {
        // Same-origin only.  For cross-origin this always throws → treat as embedded.
        // eslint-disable-next-line no-unused-expressions
        f.contentDocument;
        // Reached only when same-origin OR CSP shows an inline error frame.
        setState("embedded");
      } catch {
        // Cross-origin — a real KiviHealth render, or a browser-generated block error.
        // We can't inspect it; trust the load event.
        setState("embedded");
      }
    }, 0);
  };

  const openNewTab = () => window.open(KIVI_URL, "_blank", "noopener");

  return (
    <div className="flex flex-col h-[calc(100vh-3.5rem)] gap-3" data-testid="p-link-page">
      {/* Header strip */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold flex items-center gap-2">
            <Link2 className="w-5 h-5 text-teal-700"/> Patient Link
            <Badge className="bg-teal-100 text-teal-800 rounded-sm ml-1">via KiviHealth · M1/M2/M3</Badge>
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">
            Access KiviHealth's ABDM-approved workspace without leaving Vyomra HOS. Vyomra never sees or stores your KiviHealth credentials.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            className="rounded-sm"
            onClick={() => setKey((k) => k + 1)}
            data-testid="p-link-reload"
          >
            <RefreshCw className="w-4 h-4 mr-1"/> Reload
          </Button>
          <Button
            className="bg-teal-700 hover:bg-teal-800 rounded-sm"
            onClick={openNewTab}
            data-testid="p-link-newtab"
          >
            <ExternalLink className="w-4 h-4 mr-1"/> Open in new tab
          </Button>
        </div>
      </div>

      {/* Iframe / fallback container */}
      <div className="flex-1 border border-slate-200 rounded-sm bg-white overflow-hidden relative">
        {state === "blocked" ? (
          <div className="h-full flex flex-col items-center justify-center gap-4 p-8 text-center" data-testid="p-link-fallback">
            <div className="w-14 h-14 rounded-full bg-amber-100 border border-amber-300 flex items-center justify-center">
              <ShieldAlert className="w-7 h-7 text-amber-700"/>
            </div>
            <div className="max-w-md">
              <div className="text-lg font-semibold text-slate-900">KiviHealth cannot be embedded in this window.</div>
              <p className="text-sm text-slate-600 mt-1">
                KiviHealth's security policy (Content-Security-Policy · <code className="bg-slate-100 px-1">frame-ancestors 'self'</code>) prevents third-party sites from framing its login page.
                Click below to open KiviHealth securely in a new browser tab — your Vyomra HOS session stays intact here.
              </p>
            </div>
            <Button
              size="lg"
              className="bg-teal-700 hover:bg-teal-800 rounded-sm"
              onClick={openNewTab}
              data-testid="p-link-open-secure"
            >
              <ExternalLink className="w-4 h-4 mr-2"/> Open KiviHealth securely
            </Button>
            <div className="text-[11px] text-slate-500 mt-2">
              Tip · use <b>ctrl-tab</b> (or ⌘-<b>tab</b> on Mac) to switch between Vyomra HOS and KiviHealth.
            </div>
          </div>
        ) : (
          <>
            {state === "loading" && (
              <div className="absolute inset-0 flex items-center justify-center bg-white/80 z-10 pointer-events-none">
                <div className="text-slate-500 text-sm flex items-center gap-2">
                  <RefreshCw className="w-4 h-4 animate-spin"/> Loading KiviHealth…
                </div>
              </div>
            )}
            <iframe
              key={key}
              ref={iframeRef}
              src={KIVI_URL}
              title="KiviHealth Doctor Login"
              className="w-full h-full border-0"
              onLoad={handleLoad}
              onError={() => setState("blocked")}
              // sandbox intentionally omitted so KiviHealth's own scripts / storage work
              // referrerPolicy defaults to strict-origin-when-cross-origin
              data-testid="p-link-iframe"
            />
          </>
        )}
      </div>
    </div>
  );
}
